import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  AppShell,
  Button,
  Calendar,
  Card,
  Chip,
  Dialog,
  EmptyState,
  Icon,
  Input,
  MoneyInput,
  ProgressBar,
  SelectMenu,
  Sheet,
  Skeleton
} from '../../../ui';
import { useAtualizacaoContexto } from '../../../contextos/AtualizacaoContexto';
import { usePerfilContexto } from '../../../contextos/PerfilContexto';
import { CartaoCredito as TipoCartao } from '../../../servicos/cartaoCredito/typeCartaoCredito';
import { useCartaoCreditos } from '../../../servicos/cartaoCredito/useCartaoCredito';
import { useContas } from '../../../servicos/contas/useContas';
import { Transacao } from '../../../servicos/transacoes/typeTransacao';
import { useTransacoes } from '../../../servicos/transacoes/useTransacoes';
import { PAGAMENTO_FATURA, valorParaTexto } from '../../../utils/Utils';
import { cicloAnterior, cicloAtual, dataComDia, faturaDoCiclo, faturaFechadaAPagar, vencimentoDoCiclo } from './ciclo';
import styles from './CartaoCredito.module.css';

// Mesmos valores de bandeira do formulário anterior — contrato preservado.
const BANDEIRAS = [
  { id: 'Visa', rotulo: 'Visa' },
  { id: 'Mastercard', rotulo: 'Mastercard' },
  { id: 'Elo', rotulo: 'Elo' },
  { id: 'American Express', rotulo: 'American Express' },
  { id: 'Diners Club', rotulo: 'Diners Club' },
  { id: 'Outros', rotulo: 'Outros' }
];

const paraISO = (data: Date): string => {
  const mes = String(data.getMonth() + 1).padStart(2, '0');
  const dia = String(data.getDate()).padStart(2, '0');
  return `${data.getFullYear()}-${mes}-${dia}`;
};

const deISO = (iso: string): Date => {
  const [ano, mes, dia] = iso.split('-').map(Number);
  return new Date(ano, (mes || 1) - 1, dia || 1);
};

const rotuloData = (iso: string): string =>
  deISO(iso)
    .toLocaleDateString('pt-BR', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' })
    .replace('.', '');

/**
 * Tela de Cartões de Crédito — cada cartão com cara de cartão físico e, junto,
 * a inteligência do ciclo: fatura estimada (transações do ciclo corrente via
 * dataFechamento), limite disponível e dias de fechamento/vencimento.
 */
export default function CartaoCredito() {
  const { cartoes, loading, createCartaoCredito, updateCartaoCredito, deleteCartaoCredito } = useCartaoCreditos();
  const { perfil } = usePerfilContexto();
  const navigate = useNavigate();
  // Primeira busca ainda no ar: nunca mostrar "nenhum cartão" antes de saber.
  const carregandoLista = cartoes.length === 0 && (loading || !!perfil.carregarCartoes);
  const { atualizar, setAtualizar } = useAtualizacaoContexto();
  const { fetchTransacoes, createTransacao, loading: salvandoPagamento } = useTransacoes(false);
  const { contas } = useContas();

  const [transacoesPeriodo, setTransacoesPeriodo] = useState<Transacao[]>([]);

  const [formAberto, setFormAberto] = useState(false);
  const [editando, setEditando] = useState<TipoCartao | null>(null);
  const [excluindo, setExcluindo] = useState<TipoCartao | null>(null);
  const [menuAberto, setMenuAberto] = useState<number | null>(null);

  // Pagamento de fatura: quitação registrada na conta de origem (nunca conta
  // como despesa — as compras do cartão já foram contadas uma a uma).
  const [pagando, setPagando] = useState<TipoCartao | null>(null);
  const [pagValor, setPagValor] = useState(0);
  const [pagConta, setPagConta] = useState('');
  const [pagData, setPagData] = useState(paraISO(new Date()));
  const [pagErros, setPagErros] = useState<Record<string, string>>({});
  const [pagCalendario, setPagCalendario] = useState(false);

  const [nome, setNome] = useState('');
  const [bandeira, setBandeira] = useState('');
  const [limite, setLimite] = useState(0);
  const [fechamento, setFechamento] = useState('');
  const [vencimento, setVencimento] = useState('');
  const [erros, setErros] = useState<Record<string, string>>({});

  // A janela cobre do 1º dia de DOIS meses atrás (o ciclo ANTERIOR pode começar
  // lá, ex.: fechamento dia 4) ao último do mês seguinte; fatura fechada e
  // aberta de cada cartão filtram pelo próprio ciclo em memória.
  useEffect(() => {
    let ativo = true;
    const hoje = new Date();
    const buscar = async () => {
      const colecao = await fetchTransacoes({
        dataInicial: new Date(hoje.getFullYear(), hoje.getMonth() - 2, 1),
        dataFinal: new Date(hoje.getFullYear(), hoje.getMonth() + 2, 0)
      });
      if (ativo) setTransacoesPeriodo(colecao || []);
    };
    buscar();
    return () => {
      ativo = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [atualizar]);

  // Fecha o menu de ações ao clicar em qualquer lugar fora dele.
  useEffect(() => {
    if (menuAberto === null) return;
    const fechar = () => setMenuAberto(null);
    document.addEventListener('click', fechar);
    return () => document.removeEventListener('click', fechar);
  }, [menuAberto]);

  // Fatura estimada por cartão: despesas − estornos do ciclo corrente.
  const faturaPorCartao = useMemo(() => {
    const mapa = new Map<number, number>();
    cartoes.forEach((cartao) => {
      if (cartao.id === undefined) return;
      mapa.set(cartao.id, faturaDoCiclo(transacoesPeriodo, cartao.id, cicloAtual(cartao.dataFechamento)));
    });
    return mapa;
  }, [cartoes, transacoesPeriodo]);

  const abrirCriacao = () => {
    setEditando(null);
    setNome('');
    setBandeira('');
    setLimite(0);
    setFechamento('');
    setVencimento('');
    setErros({});
    setFormAberto(true);
  };

  const abrirEdicao = (cartao: TipoCartao) => {
    setEditando(cartao);
    setNome(cartao.nome);
    setBandeira(cartao.bandeira || '');
    setLimite(cartao.limite || 0);
    setFechamento(cartao.dataFechamento ? String(cartao.dataFechamento) : '');
    setVencimento(cartao.dataVencimento ? String(cartao.dataVencimento) : '');
    setErros({});
    setFormAberto(true);
  };

  const fecharForm = () => {
    setFormAberto(false);
    setEditando(null);
  };

  const limparErro = (campo: string) => {
    setErros((anteriores) => {
      if (!anteriores[campo]) return anteriores;
      const { [campo]: removido, ...restantes } = anteriores;
      return restantes;
    });
  };

  const handleSalvar = async () => {
    const diaFechamento = parseInt(fechamento, 10);
    const diaVencimento = parseInt(vencimento, 10);

    const novosErros: Record<string, string> = {};
    if (!nome.trim()) novosErros.nome = 'Dê um nome ao cartão.';
    if (!bandeira) novosErros.bandeira = 'Escolha a bandeira do cartão.';
    if (!limite || limite <= 0) novosErros.limite = 'Informe o limite do cartão.';
    if (!diaFechamento || diaFechamento < 1 || diaFechamento > 31) novosErros.fechamento = 'Dia entre 1 e 31.';
    if (!diaVencimento || diaVencimento < 1 || diaVencimento > 31) novosErros.vencimento = 'Dia entre 1 e 31.';

    if (Object.keys(novosErros).length > 0) {
      setErros(novosErros);
      return;
    }

    // Mesmo payload do formulário anterior: nome, bandeira, limite e dias.
    const payload = {
      nome: nome.trim(),
      bandeira,
      limite,
      dataFechamento: diaFechamento,
      dataVencimento: diaVencimento
    };

    if (editando && editando.id !== undefined) {
      await updateCartaoCredito(editando.id, payload);
    } else {
      await createCartaoCredito(payload);
    }

    fecharForm();
  };

  const confirmarExclusao = async () => {
    if (excluindo && excluindo.id !== undefined) {
      await deleteCartaoCredito([excluindo.id.toString()]);
    }
    setExcluindo(null);
  };

  // Próxima ocorrência do dia de vencimento (este mês se ainda não passou, senão o próximo).
  const proximoVencimento = (cartao: TipoCartao): Date | null => {
    if (!cartao.dataVencimento || cartao.dataVencimento < 1 || cartao.dataVencimento > 31) return null;
    const hoje = new Date();
    const nesteMes = dataComDia(hoje.getFullYear(), hoje.getMonth(), cartao.dataVencimento);
    if (hoje.getDate() <= nesteMes.getDate() && hoje.getMonth() === nesteMes.getMonth()) return nesteMes;
    return hoje.getDate() <= cartao.dataVencimento
      ? nesteMes
      : dataComDia(hoje.getFullYear(), hoje.getMonth() + 1, cartao.dataVencimento);
  };

  const abrirPagamento = (cartao: TipoCartao) => {
    setPagando(cartao);
    setPagValor(Math.max(faturaPorCartao.get(cartao.id ?? -1) || 0, 0));
    setPagConta(contas.length === 1 ? contas[0].id?.toString() ?? '' : '');
    setPagData(paraISO(new Date()));
    setPagErros({});
    setPagCalendario(false);
  };

  const confirmarPagamento = async () => {
    if (!pagando) return;

    const novosErros: Record<string, string> = {};
    if (!pagValor || pagValor <= 0) novosErros.valor = 'Informe o valor pago.';
    if (!pagConta) novosErros.conta = 'De qual conta saiu o pagamento?';
    if (!pagData) novosErros.data = 'Informe a data do pagamento.';
    if (Object.keys(novosErros).length > 0) {
      setPagErros(novosErros);
      return;
    }

    // Sai da conta de origem; SEM categoria e SEM cartaoCreditoId de propósito:
    // não entra nas análises de despesas nem infla a fatura do ciclo.
    await createTransacao({
      tipo: 'D',
      valor: pagValor,
      data: deISO(pagData),
      descricao: `Pagamento de fatura · ${pagando.nome}`,
      tipoPagamento: PAGAMENTO_FATURA,
      contaId: parseInt(pagConta, 10)
    });

    setPagando(null);
    setAtualizar((antes) => !antes);
  };

  return (
    <AppShell
      titulo="Cartões de crédito"
      subtitulo="Fatura estimada do ciclo, limite disponível e datas de cada cartão."
      acoes={
        cartoes.length > 0 ? (
          <Button icone="add" onClick={abrirCriacao}>
            Novo cartão
          </Button>
        ) : undefined
      }
    >
      {carregandoLista ? (
        <Skeleton tipo="cards" quantidade={2} altura={280} />
      ) : cartoes.length === 0 ? (
        <EmptyState
          icone="credit_card"
          titulo="Nenhum cartão cadastrado"
          descricao="Cadastre seu cartão com o limite e o dia de fechamento: o Finant estima a fatura do ciclo em tempo real e mostra quanto do limite ainda está livre."
          acao={
            <Button icone="add" onClick={abrirCriacao}>
              Cadastrar meu primeiro cartão
            </Button>
          }
        />
      ) : (
        <div className={styles.grid}>
          {cartoes.map((cartao) => {
            const fatura = faturaPorCartao.get(cartao.id ?? -1) || 0;
            const faturaPositiva = Math.max(fatura, 0);

            // Fatura FECHADA (ciclo anterior) ainda não quitada + vencimento dela.
            const janelaAnterior = cicloAnterior(cartao.dataFechamento);
            const fechada =
              cartao.id !== undefined
                ? faturaFechadaAPagar(transacoesPeriodo, cartao.id, cartao.nome, janelaAnterior)
                : 0;
            const vencimentoFechada = vencimentoDoCiclo(janelaAnterior.fim, cartao.dataVencimento);
            const janelaAtual = cicloAtual(cartao.dataFechamento);

            // Limite comprometido = fechada não paga + aberta (como no banco:
            // o limite só volta quando a fatura fechada é quitada).
            const comprometido = fechada + faturaPositiva;
            const disponivel = (cartao.limite || 0) - comprometido;
            const percentual = cartao.limite > 0 ? (comprometido / cartao.limite) * 100 : 0;

            const verCiclo = (janela: { inicio: Date; fim: Date }) =>
              navigate('/movimentacoes', {
                state: {
                  cartaoIds: [cartao.id?.toString() ?? ''],
                  dataInicial: paraISO(janela.inicio),
                  dataFinal: paraISO(janela.fim)
                }
              });

            return (
              <Card key={cartao.id} className={styles.cardCartao}>
                <div className={styles.face}>
                  <div className={styles.faceDetalhe} />
                  <div className={styles.faceTopo}>
                    <span className={styles.faceNome}>{cartao.nome}</span>
                    <div className={styles.menuArea}>
                      <button
                        type="button"
                        className={`${styles.menuBotao} ${styles.menuBotaoClaro}`}
                        aria-label="Ações do cartão"
                        onClick={(evento) => {
                          evento.stopPropagation();
                          setMenuAberto(menuAberto === cartao.id ? null : cartao.id ?? null);
                        }}
                      >
                        <Icon nome="more_vert" tamanho={18} />
                      </button>
                      {menuAberto === cartao.id && (
                        <div className={styles.menu}>
                          <button type="button" className={styles.menuItem} onClick={() => abrirEdicao(cartao)}>
                            <Icon nome="edit" tamanho={16} />
                            Editar
                          </button>
                          <button
                            type="button"
                            className={`${styles.menuItem} ${styles.menuItemPerigo}`}
                            onClick={() => setExcluindo(cartao)}
                          >
                            <Icon nome="delete" tamanho={16} />
                            Excluir
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                  <div className={styles.faceChip} />
                  <div className={styles.faceNumero}>•••• &nbsp;•••• &nbsp;•••• &nbsp;••••</div>
                  <div className={styles.faceRodape}>
                    <span className={styles.faceCredito}>Crédito</span>
                    <span className={styles.faceBandeira}>{cartao.bandeira || 'Cartão'}</span>
                  </div>
                </div>

                <div className={styles.info}>
                  {fechada > 0 && (
                    <button
                      type="button"
                      className={styles.faturaFechada}
                      onClick={() => verCiclo(janelaAnterior)}
                      title="Ver as compras dessa fatura"
                    >
                      <Icon nome="event_busy" tamanho={16} />
                      <span className={styles.faturaFechadaTexto}>
                        Fatura fechada
                        {vencimentoFechada
                          ? ` · vence ${vencimentoFechada.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' })}`
                          : ''}
                      </span>
                      <span className={styles.faturaFechadaValor}>{valorParaTexto(fechada, true)}</span>
                      <Icon nome="chevron_right" tamanho={16} />
                    </button>
                  )}
                  <button
                    type="button"
                    className={styles.faturaLinha}
                    onClick={() => verCiclo(janelaAtual)}
                    title="Ver as compras do ciclo atual"
                  >
                    <span className={styles.faturaRotulo}>Fatura aberta (ciclo atual)</span>
                    <span className={styles.faturaValor}>{valorParaTexto(faturaPositiva, true)}</span>
                    <Icon nome="chevron_right" tamanho={16} />
                  </button>
                  <ProgressBar percentual={percentual} tom="auto-despesa" />
                  <div className={styles.limitesLinha}>
                    <span className={`${styles.disponivel} ${disponivel < 0 ? styles.estourado : ''}`}>
                      {valorParaTexto(disponivel, true)} disponível
                    </span>
                    <span className={styles.limiteTotal}>Limite {valorParaTexto(cartao.limite, true)}</span>
                  </div>
                  <div className={styles.chips}>
                    {!!cartao.dataFechamento && <Chip rotulo={`Fecha dia ${cartao.dataFechamento}`} icone="event_upcoming" />}
                    {!!cartao.dataVencimento && <Chip rotulo={`Vence dia ${cartao.dataVencimento}`} icone="event_available" />}
                  </div>
                  <div className={styles.pagamentoAcao}>
                    <Button variante="suave" tamanho="sm" icone="price_check" onClick={() => abrirPagamento(cartao)}>
                      Registrar pagamento
                    </Button>
                  </div>
                </div>
              </Card>
            );
          })}
        </div>
      )}

      <Sheet
        aberto={formAberto}
        onFechar={fecharForm}
        titulo={editando ? 'Editar cartão' : 'Novo cartão'}
        subtitulo={
          editando
            ? 'Ajuste limite, bandeira ou as datas do ciclo.'
            : 'Com o dia de fechamento certo, a fatura estimada fica precisa.'
        }
        rodape={
          <>
            <Button variante="contorno" onClick={fecharForm}>
              Cancelar
            </Button>
            <Button icone="check" carregando={loading} onClick={handleSalvar}>
              {editando ? 'Salvar alterações' : 'Adicionar cartão'}
            </Button>
          </>
        }
      >
        <div className={styles.form}>
          <Input
            label="Nome do cartão"
            valor={nome}
            onChange={(valor) => {
              setNome(valor);
              limparErro('nome');
            }}
            placeholder="Ex.: Nubank Ultravioleta, Inter Gold..."
            icone="credit_card"
            erro={erros.nome}
            obrigatorio
            autoFocus
          />
          <SelectMenu
            label="Bandeira"
            valor={bandeira}
            onChange={(valor) => {
              setBandeira(valor);
              limparErro('bandeira');
            }}
            opcoes={BANDEIRAS}
            placeholder="Escolha a bandeira"
            erro={erros.bandeira}
          />
          <MoneyInput
            label="Limite do cartão"
            valor={limite}
            onChange={(valor) => {
              setLimite(valor);
              limparErro('limite');
            }}
            erro={erros.limite}
            obrigatorio
          />
          <div className={styles.linhaDupla}>
            <Input
              label="Dia do fechamento"
              valor={fechamento}
              onChange={(valor) => {
                setFechamento(valor);
                limparErro('fechamento');
              }}
              tipo="number"
              placeholder="1 a 31"
              icone="event_upcoming"
              erro={erros.fechamento}
              dica="Quando a fatura fecha"
              obrigatorio
            />
            <Input
              label="Dia do vencimento"
              valor={vencimento}
              onChange={(valor) => {
                setVencimento(valor);
                limparErro('vencimento');
              }}
              tipo="number"
              placeholder="1 a 31"
              icone="event_available"
              erro={erros.vencimento}
              dica="Quando a fatura vence"
              obrigatorio
            />
          </div>
        </div>
      </Sheet>

      <Dialog
        aberto={excluindo !== null}
        onFechar={() => setExcluindo(null)}
        titulo="Excluir cartão"
        descricao={`Excluir "${excluindo?.nome}"? As movimentações associadas serão desvinculadas, mas o histórico permanece.`}
        acoes={
          <>
            <Button variante="contorno" onClick={() => setExcluindo(null)}>
              Cancelar
            </Button>
            <Button variante="perigo" icone="delete" carregando={loading} onClick={confirmarExclusao}>
              Excluir
            </Button>
          </>
        }
      />

      <Sheet
        aberto={pagando !== null}
        onFechar={() => setPagando(null)}
        titulo={`Pagamento de fatura${pagando ? ` · ${pagando.nome}` : ''}`}
        subtitulo="Registra a saída da conta na data do pagamento. As compras do cartão já foram contadas uma a uma, então este lançamento não soma como despesa."
        largura={440}
        rodape={
          <>
            <Button variante="fantasma" onClick={() => setPagando(null)}>
              Cancelar
            </Button>
            <Button icone="check" carregando={salvandoPagamento} onClick={confirmarPagamento}>
              Registrar pagamento
            </Button>
          </>
        }
      >
        <div className={styles.pagForm}>
          <MoneyInput
            label="Valor pago"
            valor={pagValor}
            onChange={(valor) => {
              setPagValor(valor);
              setPagErros((antes) => ({ ...antes, valor: '' }));
            }}
            erro={pagErros.valor || undefined}
          />
          <SelectMenu
            label="Conta de origem"
            valor={pagConta}
            onChange={(valor) => {
              setPagConta(valor);
              setPagErros((antes) => ({ ...antes, conta: '' }));
            }}
            opcoes={contas.map((conta) => ({
              id: conta.id?.toString() ?? '',
              rotulo: conta.descricao,
              icone: 'account_balance'
            }))}
            placeholder="De onde saiu o dinheiro"
            erro={pagErros.conta || undefined}
          />
          <div className={styles.pagDataCampo}>
            <span className={styles.pagRotulo}>Data do pagamento</span>
            <div className={styles.pagDataLinha}>
              <Chip rotulo="Hoje" icone="today" ativo={pagData === paraISO(new Date())} onClick={() => setPagData(paraISO(new Date()))} />
              {pagando && proximoVencimento(pagando) && (
                <Chip
                  rotulo={`No vencimento (dia ${pagando.dataVencimento})`}
                  icone="event_available"
                  ativo={pagData === paraISO(proximoVencimento(pagando) as Date)}
                  onClick={() => setPagData(paraISO(proximoVencimento(pagando) as Date))}
                />
              )}
              <div className={styles.pagDataAncora}>
                <button type="button" className={styles.pagDataBotao} onClick={() => setPagCalendario((antes) => !antes)}>
                  <Icon nome="event" tamanho={18} />
                  <span>{rotuloData(pagData)}</span>
                  <Icon nome="expand_more" tamanho={16} />
                </button>
                {pagCalendario && (
                  <div className={styles.pagCalendario}>
                    <Calendar
                      modo="unica"
                      inicio={deISO(pagData)}
                      onChange={(novaData) => {
                        setPagData(paraISO(novaData));
                        setPagCalendario(false);
                      }}
                    />
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </Sheet>
    </AppShell>
  );
}
